import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileArrowDown, ChatCircleDots, UserCircle } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { jobsApi, applicationsApi, APPLICATION_STATUS_LABELS, timeAgo } from "@/lib/jobsApi";
import { toast } from "sonner";

const STATUSES = ["submitted", "under_review", "shortlisted", "interview", "accepted", "rejected"];

/** Employer's applicant dashboard for a single job. */
export default function JobApplicants() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, d] = await Promise.all([
        jobsApi.detail(jobId).catch(() => null),
        jobsApi.applicants(jobId, filter || undefined),
      ]);
      setJob(j);
      setApps(d.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [jobId, filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (appId, status) => {
    try {
      await applicationsApi.setStatus(appId, { status });
      toast.success(`Marked as ${APPLICATION_STATUS_LABELS[status]}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: apps.filter((a) => a.status === s).length }), {});

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-4" data-testid="applicants-page">
      <button onClick={() => navigate("/jobs")} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> All jobs
      </button>
      <div>
        <h1 className="font-heading text-3xl font-black">Applicants</h1>
        {job && <p className="text-white/60 mt-1">{job.title} · {job.employer_company_name}</p>}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`applicant-filter-${s}`}
            onClick={() => setFilter(filter === s ? "" : s)}
            className={`p-3 rounded-lg border transition ${
              filter === s ? "bg-blue-500/20 border-blue-500/50 text-blue-100" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
            }`}
          >
            <div className="font-heading text-xl font-black">{counts[s] || 0}</div>
            <div className="uppercase tracking-widest text-[10px] font-bold mt-1">
              {APPLICATION_STATUS_LABELS[s].replace(" scheduled", "")}
            </div>
          </button>
        ))}
      </div>

      {loading && <div className="text-white/50">Loading…</div>}
      {!loading && apps.length === 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardContent className="p-8 text-center text-white/50">No applicants{filter ? ` in ${APPLICATION_STATUS_LABELS[filter]}` : " yet"}.</CardContent>
        </Card>
      )}
      {apps.map((a) => (
        <Card key={a.application_id} className="bg-white/[0.03] border-white/10" data-testid={`applicant-${a.application_id}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3 flex-wrap">
              {a.applicant?.picture ? (
                <img src={a.applicant.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                  <UserCircle size={28} className="text-white/40" weight="duotone" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-bold">{a.applicant?.name || "Candidate"}</h3>
                  <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 text-[10px] uppercase font-black">
                    {APPLICATION_STATUS_LABELS[a.status]}
                  </span>
                </div>
                {a.applicant?.headline && <p className="text-sm text-white/70">{a.applicant.headline}</p>}
                {a.applicant?.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {a.applicant.skills.slice(0, 6).map((s) => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60">{s}</span>
                    ))}
                  </div>
                )}
                {a.cover_letter && (
                  <p className="text-sm text-white/70 mt-2 line-clamp-3 whitespace-pre-line">{a.cover_letter}</p>
                )}
                <p className="text-xs text-white/40 mt-2">Applied {timeAgo(a.created_at)}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Select value={a.status} onValueChange={(v) => setStatus(a.application_id, v)}>
                  <SelectTrigger data-testid={`applicant-status-${a.application_id}`} className="bg-white/5 border-white/10 w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{APPLICATION_STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-1.5">
                  {a.cv_url && (
                    <a href={a.cv_url} target="_blank" rel="noreferrer" data-testid={`applicant-cv-${a.application_id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/15 text-xs hover:bg-white/5">
                      <FileArrowDown size={12} weight="bold" /> CV
                    </a>
                  )}
                  <button
                    onClick={() => navigate(`/messages/${a.application_id}`)}
                    data-testid={`applicant-msg-${a.application_id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/15 text-xs hover:bg-white/5"
                  >
                    <ChatCircleDots size={12} weight="bold" /> Message
                  </button>
                  <button
                    onClick={() => navigate(`/career/${a.applicant_id}`)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/15 text-xs hover:bg-white/5"
                  >
                    <UserCircle size={12} weight="bold" /> Profile
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
